/** Portail — « Mes favoris » (ajout rapide au panier) et « Commandes récurrentes » (modèles enregistrés depuis le panier). */
import { useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Choice, QuantityInput, SelectField, Table, TextField, useToast } from '@myron/design-system';
import { BellRing, FilePlus2, Heart, RotateCcw, Save, ShoppingCart, Trash2 } from 'lucide-react';
import { del, messageErreur } from '../../../shared/api/client';
import { SiPermission } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete } from '../../../shared/ui/composants';
import { dateCourte, LIBELLE_CONDITIONNEMENT } from '../../../shared/ui/format';
import { clesFidelite, LIBELLE_FREQUENCE, useAjoutPanier, useFavoris, useModeles, useMutationsModeles, type Favori, type Frequence, type ModeleCommande } from './api';

// ── Favoris ──────────────────────────────────────────────────────────────────────────────────────
function LigneFavori({ f }: { f: Favori }) {
  const [quantite, setQuantite] = useState(f.minimum);
  const ajout = useAjoutPanier();
  const qc = useQueryClient();
  const toast = useToast();
  const [retrait, setRetrait] = useState(false);
  return (
    <Card as="article" aria-label={f.libelle}>
      <div className="my-form-ligne">
        <div className="my-pile my-pile--serre" style={{ flex: 2, minWidth: 200 }}>
          <strong>{f.libelle}</strong>
          <span className="my-petit my-secondaire">{f.sku} · {LIBELLE_CONDITIONNEMENT[f.conditionnementType]}{f.unitesBase ? ` de ${f.unitesBase} u.` : ''}</span>
          {!f.disponible && <Badge ton="alerte">Indisponible à la vente</Badge>}
        </div>
        {f.disponible && <QuantityInput label={`Quantité — ${f.libelle}`} valeur={quantite} min={f.minimum} pas={f.multiple} onChange={setQuantite} />}
        <SiPermission permission="panier.gerer">
          {f.disponible && (
            <Button
              icone={ShoppingCart}
              chargement={ajout.isPending}
              aria-label={`Ajouter au panier : ${f.libelle}`}
              onClick={() => ajout.mutate({ produitId: f.produitId, conditionnementType: f.conditionnementType, quantite }, {
                onSuccess: () => toast({ ton: 'succes', titre: 'Ajouté au panier', texte: `${quantite} × ${f.libelle}` }),
                onError: (e) => toast({ ton: 'erreur', titre: 'Ajout impossible', texte: messageErreur(e) }),
              })}
            >
              Ajouter au panier
            </Button>
          )}
        </SiPermission>
        <Button variante="tertiaire" icone={Trash2} aria-label={`Retirer des favoris : ${f.libelle}`} onClick={() => setRetrait(true)}>Retirer</Button>
      </div>
      <Confirmation
        ouverte={retrait}
        titre="Retirer ce favori ?"
        texte={f.libelle}
        libelleAction="Retirer"
        onFermer={() => setRetrait(false)}
        onConfirmer={async () => { try { await del(`/pro/favoris/${f.id}`); setRetrait(false); void qc.invalidateQueries({ queryKey: clesFidelite.favoris }); } catch (e) { toast({ ton: 'erreur', titre: 'Retrait impossible', texte: messageErreur(e) }); } }}
      />
    </Card>
  );
}

export function MesFavoris() {
  const q = useFavoris();
  return (
    <div className="my-pile my-pile--large">
      <EntetePage titre="Mes favoris" sousTitre="Vos produits habituels, à ajouter au panier en un clic. Les prix sont calculés dans le panier selon vos conditions." />
      <EtatRequete
        requete={q}
        vide={(d) => d.length === 0}
        titreVide="Aucun favori"
        texteVide="Depuis une fiche produit, utilisez « Ajouter aux favoris »."
        actionVide={<Link to="/pro/catalogue" className="my-btn my-btn--secondaire"><Heart className="my-icon" aria-hidden />Parcourir le catalogue</Link>}
        enfants={(d) => <div className="my-pile">{d.map((f) => <LigneFavori key={f.id} f={f} />)}</div>}
      />
    </div>
  );
}

// ── Modèles de commande ───────────────────────────────────────────────────────────────────────────
const OPTIONS_FREQUENCE = (Object.keys(LIBELLE_FREQUENCE) as Frequence[]).map((f) => ({ value: f, label: LIBELLE_FREQUENCE[f] }));

function FormulaireModele() {
  const [nom, setNom] = useState('');
  const [frequence, setFrequence] = useState<Frequence>('AUCUNE');
  const m = useMutationsModeles();
  const toast = useToast();
  return (
    <Card as="section" aria-labelledby="t-nouveau-modele">
      <form className="my-pile" onSubmit={(e) => { e.preventDefault(); m.creer.mutate({ nom, frequence, depuisPanier: true }, { onSuccess: (r) => { setNom(''); toast({ ton: 'succes', titre: 'Modèle enregistré', texte: `${r.lignes.length} ligne(s) reprises du panier.` }); } }); }}>
        <h2 id="t-nouveau-modele" className="my-titre-4">Enregistrer mon panier comme modèle</h2>
        <div className="my-form-ligne">
          <TextField label="Nom du modèle" value={nom} onChange={(e) => setNom(e.target.value)} required minLength={2} maxLength={80} placeholder="Ex. Réassort du lundi" />
          <SelectField label="Rappel" value={frequence} onChange={(e) => setFrequence(e.target.value as Frequence)} options={OPTIONS_FREQUENCE} aide="Un rappel vous est envoyé à l’échéance ; aucune commande n’est passée automatiquement." />
        </div>
        <ErreurFormulaire erreur={m.creer.error} />
        <div><Button type="submit" icone={Save} chargement={m.creer.isPending} disabled={nom.trim().length < 2}>Enregistrer le panier</Button></div>
      </form>
    </Card>
  );
}

function CarteModele({ modele }: { modele: ModeleCommande }) {
  const m = useMutationsModeles();
  const toast = useToast();
  const [suppression, setSuppression] = useState(false);
  const [remplacer, setRemplacer] = useState(false);
  const indisponibles = modele.lignes.filter((l) => !l.disponible).length;
  return (
    <Card as="article" aria-label={`Modèle ${modele.nom}`} data-testid="modele">
      <div className="my-pile">
        <div className="my-ligne" style={{ justifyContent: 'space-between' }}>
          <h2 className="my-titre-4">{modele.nom}</h2>
          <div className="my-ligne">
            {modele.aCommander && <Badge ton="alerte">À commander</Badge>}
            {!modele.actif && <Badge>Rappel suspendu</Badge>}
            <Badge ton="sable">{LIBELLE_FREQUENCE[modele.frequence]}</Badge>
          </div>
        </div>
        {modele.aCommander && <Alert ton="alerte" titre="Échéance atteinte">Rechargez ce modèle dans votre panier pour passer commande{modele.notifieLe ? ` (rappel envoyé le ${dateCourte(modele.notifieLe)}, simulation)` : ''}. Aucune commande n’est passée automatiquement.</Alert>}
        {modele.prochaineEcheance && !modele.aCommander && <p className="my-petit my-secondaire"><BellRing className="my-icon" aria-hidden /> Prochain rappel le {dateCourte(modele.prochaineEcheance)}</p>}
        <Table
          legende={`Lignes du modèle ${modele.nom}`}
          cle={(l) => `${l.produitId}-${l.conditionnementType}`}
          lignes={modele.lignes}
          colonnes={[
            { cle: 'p', titre: 'Produit', rendu: (l) => <span>{l.libelle} {!l.disponible && <Badge ton="alerte">Indisponible</Badge>}</span> },
            { cle: 'c', titre: 'Conditionnement', rendu: (l) => LIBELLE_CONDITIONNEMENT[l.conditionnementType] },
            { cle: 'q', titre: 'Quantité', num: true, rendu: (l) => l.quantite },
          ]}
        />
        {indisponibles > 0 && <p className="my-petit my-secondaire">{indisponibles} ligne(s) indisponible(s) seront ignorées au rechargement.</p>}
        <div className="my-ligne">
          <SiPermission permission="panier.gerer">
            <Choice label="Remplacer le panier actuel" checked={remplacer} onChange={(e) => setRemplacer(e.target.checked)} />
            <Button
              icone={RotateCcw}
              chargement={m.charger.isPending}
              aria-label={`Recharger dans le panier : ${modele.nom}`}
              onClick={() => m.charger.mutate({ id: modele.id, mode: remplacer ? 'REMPLACER' : 'AJOUTER' }, {
                onSuccess: (r) => toast({ ton: 'succes', titre: 'Panier mis à jour', texte: `${r.nbLignesPanier} ligne(s) dans le panier${r.ignorees.length ? ` ; ${r.ignorees.length} ignorée(s)` : ''}.` }),
                onError: (e) => toast({ ton: 'erreur', titre: 'Rechargement impossible', texte: messageErreur(e) }),
              })}
            >
              Recharger dans le panier
            </Button>
          </SiPermission>
          {modele.frequence !== 'AUCUNE' && (
            <Button variante="secondaire" compact onClick={() => m.modifier.mutate({ m: modele, champs: { actif: !modele.actif } })} chargement={m.modifier.isPending}>
              {modele.actif ? 'Suspendre le rappel' : 'Réactiver le rappel'}
            </Button>
          )}
          <Button variante="tertiaire" icone={Trash2} onClick={() => setSuppression(true)} aria-label={`Supprimer le modèle ${modele.nom}`}>Supprimer</Button>
        </div>
      </div>
      <Confirmation ouverte={suppression} titre="Supprimer ce modèle ?" texte={modele.nom} libelleAction="Supprimer" danger chargement={m.supprimer.isPending} erreur={m.supprimer.error}
        onFermer={() => setSuppression(false)} onConfirmer={() => m.supprimer.mutate(modele.id, { onSuccess: () => setSuppression(false) })} />
    </Card>
  );
}

export function MesModeles() {
  const q = useModeles();
  return (
    <div className="my-pile my-pile--large">
      <EntetePage titre="Commandes récurrentes" sousTitre="Enregistrez un panier type, rechargez-le en un clic et recevez un rappel à l’échéance." actions={<Link to="/pro/panier" className="my-btn my-btn--secondaire"><ShoppingCart className="my-icon" aria-hidden />Mon panier</Link>} />
      <FormulaireModele />
      <EtatRequete
        requete={q}
        vide={(d) => d.length === 0}
        titreVide="Aucun modèle"
        texteVide="Composez votre panier puis enregistrez-le ci-dessus."
        actionVide={<Link to="/pro/catalogue" className="my-btn my-btn--secondaire"><FilePlus2 className="my-icon" aria-hidden />Composer un panier</Link>}
        enfants={(d) => <div className="my-pile">{d.map((x) => <CarteModele key={x.id} modele={x} />)}</div>}
      />
    </div>
  );
}
